import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Form, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaPrint, FaArrowLeft, FaCheck, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import estacionesImpresionApi from '../../../services/api/estacionesImpresionApi';

const EstacionesImpresion = () => {
    const navigate = useNavigate();
    const [estaciones, setEstaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentEstacionId, setCurrentEstacionId] = useState(null);
    
    const [formData, setFormData] = useState({
        codigo: '',
        alias: '',
        persona_encargada: ''
    });

    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));

    useEffect(() => {
        if (selectedProject?.id) {
            fetchEstaciones();
        } else {
            toast.warning('Por favor seleccione un proyecto genérico primero.');
        }
    }, [selectedProject?.id]);

    const fetchEstaciones = async () => {
        setLoading(true);
        try {
            const response = await estacionesImpresionApi.getAll(selectedProject.id);
            if (response.data.success) {
                setEstaciones(response.data.data);
            }
        } catch (error) {
            toast.error('Error al cargar estaciones de impresión');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setFormData({ codigo: '', alias: '', persona_encargada: '' });
        setIsEditing(false);
        setCurrentEstacionId(null);
    };

    const handleShow = () => setShowModal(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.codigo || !formData.alias || !formData.persona_encargada) {
            toast.warning('Por favor llene todos los campos obligatorios');
            return;
        }

        try {
            setLoading(true);
            if (isEditing) {
                await estacionesImpresionApi.update(selectedProject.id, currentEstacionId, formData);
                toast.success('Estación actualizada exitosamente');
            } else {
                await estacionesImpresionApi.create(selectedProject.id, formData);
                toast.success('Estación creada exitosamente');
            }
            handleClose();
            fetchEstaciones();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Ocurrió un error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (estacion) => {
        setFormData({
            codigo: estacion.codigo,
            alias: estacion.alias,
            persona_encargada: estacion.persona_encargada
        });
        setIsEditing(true);
        setCurrentEstacionId(estacion.id);
        handleShow();
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta estación de impresión?')) {
            try {
                setLoading(true);
                await estacionesImpresionApi.delete(selectedProject.id, id);
                toast.success('Estación eliminada');
                fetchEstaciones();
            } catch (error) {
                toast.error('Error al eliminar estación');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <>
            <style>
                {`
                  /* Fondo degradado */
                  .dh-maintenance-bg {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    min-height: 100vh;
                  }
                  
                  /* Header principal */
                  .dh-main-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 2rem 0;
                    margin-bottom: 2rem;
                    border-radius: 0 0 30px 30px;
                    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
                  }
                  
                  .dh-main-title {
                    font-size: 2.2rem;
                    font-weight: 700;
                    margin: 0;
                  }
                  
                  .dh-main-subtitle {
                    font-size: 1.1rem;
                    opacity: 0.9;
                    margin: 0.5rem 0 0 0;
                  }
                  
                  /* Cards con Digital Harmony */
                  .dh-card {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: none;
                    overflow: hidden;
                    transition: all 0.3s ease;
                  }
                  
                  .dh-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
                  }
                  
                  .dh-card-body {
                    padding: 2rem;
                  }
                  
                  /* Botones Digital Harmony */
                  .btn-dh-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border: none;
                    border-radius: 12px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    color: white;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                  }
                  
                  .btn-dh-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
                    color: white;
                  }
                  
                  .btn-dh-secondary {
                    background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
                    border: none;
                    border-radius: 12px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    color: white;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
                  }
                  
                  .btn-dh-secondary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(108, 117, 125, 0.4);
                    color: white;
                  }
                  
                  .btn-dh-danger {
                    background: linear-gradient(135deg, #dc3545 0%, #e83e8c 100%);
                    border: none;
                    border-radius: 12px;
                    padding: 0.5rem 1rem;
                    font-weight: 600;
                    color: white;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
                  }
                  
                  .btn-dh-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
                    color: white;
                  }
                  
                  /* Tabla Digital Harmony */
                  .dh-table {
                    background: white;
                    border-radius: 15px;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                  }
                  
                  .dh-table thead th {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    padding: 1rem;
                    font-weight: 600;
                    font-size: 0.95rem;
                  }
                  
                  .dh-table tbody td {
                    padding: 1rem;
                    border-bottom: 1px solid #f1f3f4;
                    vertical-align: middle;
                  }
                  
                  .dh-table tbody tr:hover {
                    background: #f8f9fa;
                  }
                  
                  .dh-fade-in {
                      animation: fadeInUp 0.6s ease-out;
                  }
                  
                  @keyframes fadeInUp {
                      from { opacity: 0; transform: translateY(30px); }
                      to { opacity: 1; transform: translateY(0); }
                  }
                `}
            </style>

            <div className="dh-maintenance-bg">
                <ToastContainer position="top-right" autoClose={3000} />
                
                <div className="dh-main-header">
                    <Container>
                        <Row className="align-items-center">
                            <Col md={8}>
                                <div className="d-flex align-items-center">
                                    <Button
                                        variant="outline-light"
                                        className="me-3 btn-dh-secondary"
                                        onClick={() => navigate('/mantenimientos')}
                                    >
                                        <FaArrowLeft className="me-2" />
                                        Volver
                                    </Button>
                                    <div>
                                        <h1 className="dh-main-title">
                                            <FaPrint className="me-3" />
                                            Estaciones de Impresión Ticket
                                        </h1>
                                        <p className="dh-main-subtitle">
                                            Gestiona las estaciones de impresión de tickets físicos
                                        </p>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="text-end">
                                <Button 
                                    className="btn-dh-primary d-inline-flex align-items-center" 
                                    onClick={handleShow} 
                                    disabled={!selectedProject?.id}
                                >
                                    <FaPlus className="me-2" />
                                    Nueva Estación
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </div>

                <Container className="dh-fade-in pb-5">
                    <Card className="dh-card">
                        <Card.Body className="dh-card-body">
                            {loading && !showModal ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : estaciones.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <FaPrint size={48} className="mb-3 opacity-50" />
                                    <p>No hay estaciones de impresión configuradas para este proyecto.</p>
                                </div>
                            ) : (
                                <Table responsive hover className="dh-table mb-0">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Alias / Nombre</th>
                                            <th>Persona Encargada</th>
                                            <th>Creado</th>
                                            <th className="text-end">Acciones</th>
                                        </tr>
                                    </thead>

                            <tbody>
                                {estaciones.map(estacion => (
                                    <tr key={estacion.id}>
                                        <td className="align-middle fw-bold">{estacion.codigo}</td>
                                        <td className="align-middle">{estacion.alias}</td>
                                        <td className="align-middle">{estacion.persona_encargada}</td>
                                        <td className="align-middle">
                                            {new Date(estacion.created_at).toLocaleDateString()}
                                        </td>
                                            <td className="align-middle text-end">
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button variant="outline-primary" size="sm" onClick={() => handleEdit(estacion)}>
                                                        <FaEdit />
                                                    </Button>
                                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(estacion.id)}>
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Container>

            {showModal && (
                <div className="modal show d-block dh-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FaPrint className="me-2" />
                                    {isEditing ? 'Editar Estación' : 'Nueva Estación de Impresión'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
                            </div>
                            <div className="modal-body">
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={12}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">
                                                    <FaPrint className="me-2" />
                                                    Código Único (Machine ID) <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    className="dh-form-control"
                                                    name="codigo"
                                                    value={formData.codigo}
                                                    onChange={handleChange}
                                                    placeholder="Ej: PC-01"
                                                    required
                                                />
                                                <Form.Text className="text-muted">
                                                    Este código EXACTO debe existir en el .env del Cliente de node (ESTACION_CODIGO=x)
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    
                                    <Row>
                                        <Col md={12}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">
                                                    <FaPrint className="me-2" />
                                                    Alias (Nombre Amigable) <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    className="dh-form-control"
                                                    name="alias"
                                                    value={formData.alias}
                                                    onChange={handleChange}
                                                    placeholder="Ej: PC en la sala 1"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">
                                                    <FaPrint className="me-2" />
                                                    Persona Encargada <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    className="dh-form-control"
                                                    name="persona_encargada"
                                                    value={formData.persona_encargada}
                                                    onChange={handleChange}
                                                    placeholder="Ej: Juan Perez"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end mt-4 gap-2">
                                        <Button className="btn-dh-secondary" onClick={handleClose}>
                                            <FaTimes className="me-2" />
                                            Cancelar
                                        </Button>
                                        <Button className="btn-dh-primary" type="submit" disabled={loading}>
                                            <FaCheck className="me-2" />
                                            {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Guardar'}
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default EstacionesImpresion;
